/** Fegyver-archetípusok — a GDD 1. pontjának adatvezérelt megfelelői. */

import { secs, type DamageKind } from '../core/contracts'
import type { AmmoType } from '../player/player'

export interface WeaponArchetype {
  id: WeaponId
  name: string
  slot: number
  ammo: AmmoType | null
  ammoPerShot: number
  damage: [number, number]
  damageKind: DamageKind
  pellets: number
  spreadDeg: number
  range: number
  fireInterval: number
  automatic: boolean
  recoil: number
  /** `projectile` esetén a `PROJECTILES` tábla kulcsa. */
  projectile?: 'rocket' | 'plasmaBolt'
  muzzleColor: number
  muzzleRadius: number
  sound: string
  /** Fegyverenkénti keverési karakter, hogy lövésre is felismerhető legyen. */
  soundVolume: number
  soundPitch: number
  soundPitchJitter: number
  /** Elsütéskor adott, a viewmodel-visszarúgástól független kamerarázás. */
  cameraShake: number
  /** Hitscan nyomjelző vastagsága; lövedékes és közelharci fegyvernél nincs. */
  tracerWidth?: number
}

export type WeaponId = 'fist' | 'pistol' | 'shotgun' | 'chaingun' | 'rocket' | 'plasma'

export const WEAPON_ORDER: readonly WeaponId[] = ['fist', 'pistol', 'shotgun', 'chaingun', 'rocket', 'plasma']

export const WEAPONS: Record<WeaponId, WeaponArchetype> = {
  fist: {
    id: 'fist', name: 'ÖKÖL', slot: 1, ammo: null, ammoPerShot: 0,
    damage: [18, 24], damageKind: 'melee', pellets: 1, spreadDeg: 5, range: 1.7,
    fireInterval: secs(0.42), automatic: false, recoil: 0.35,
    muzzleColor: 0xffd98a, muzzleRadius: 0, sound: 'melee',
    soundVolume: 0.68, soundPitch: 0.9, soundPitchJitter: 0.04, cameraShake: 0.012,
  },
  pistol: {
    id: 'pistol', name: 'SZOLGÁLATI PISZTOLY', slot: 2, ammo: 'bullet', ammoPerShot: 1,
    damage: [18, 18], damageKind: 'bullet', pellets: 1, spreadDeg: 0, range: 60,
    fireInterval: secs(0.24), automatic: true, recoil: 0.7,
    muzzleColor: 0xffd98a, muzzleRadius: 5, sound: 'pistol',
    soundVolume: 0.76, soundPitch: 1.05, soundPitchJitter: 0.04, cameraShake: 0.035, tracerWidth: 0.016,
  },
  shotgun: {
    id: 'shotgun', name: 'TÖRŐ SÖRÉTES', slot: 3, ammo: 'shell', ammoPerShot: 1,
    // A GDD szerepleírása szerint 12 egységen túl már pazarló; a 8°-os
    // szórással együtt ez egyértelműen közelharci burstfegyverré teszi.
    damage: [10, 10], damageKind: 'pellet', pellets: 8, spreadDeg: 8, range: 12,
    fireInterval: secs(0.85), automatic: false, recoil: 3.2,
    muzzleColor: 0xffd98a, muzzleRadius: 7, sound: 'shotgun',
    soundVolume: 1.0, soundPitch: 0.82, soundPitchJitter: 0.025, cameraShake: 0.13, tracerWidth: 0.03,
  },
  chaingun: {
    id: 'chaingun', name: 'ROTÁCIÓS GÉPÁGYÚ', slot: 4, ammo: 'bullet', ammoPerShot: 1,
    damage: [12, 12], damageKind: 'bullet', pellets: 1, spreadDeg: 0, range: 60,
    fireInterval: secs(0.09), automatic: true, recoil: 0.45,
    muzzleColor: 0xffd98a, muzzleRadius: 4, sound: 'chaingun',
    soundVolume: 0.6, soundPitch: 1.18, soundPitchJitter: 0.08, cameraShake: 0.022, tracerWidth: 0.012,
  },
  rocket: {
    id: 'rocket', name: 'RAKÉTAVETŐ', slot: 5, ammo: 'rocket', ammoPerShot: 1,
    damage: [110, 110], damageKind: 'explosion', pellets: 1, spreadDeg: 0, range: 45,
    fireInterval: secs(0.9), automatic: false, recoil: 4,
    projectile: 'rocket', muzzleColor: 0xffd98a, muzzleRadius: 8, sound: 'rocketFire',
    soundVolume: 1.0, soundPitch: 0.72, soundPitchJitter: 0.02, cameraShake: 0.18,
  },
  plasma: {
    id: 'plasma', name: 'PLAZMAVETŐ', slot: 6, ammo: 'cell', ammoPerShot: 2,
    damage: [32, 32], damageKind: 'plasma', pellets: 1, spreadDeg: 0, range: 50,
    fireInterval: secs(0.12), automatic: true, recoil: 0.25,
    projectile: 'plasmaBolt', muzzleColor: 0x8affff, muzzleRadius: 5, sound: 'plasmaFire',
    soundVolume: 0.66, soundPitch: 1.32, soundPitchJitter: 0.06, cameraShake: 0.018,
  },
}
